import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, from, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { SubBrand } from './sub-brands.model';
import { Page } from '../../../models/page-wrapper.model';
import { environment } from '../../../../environments/environment';
import { PickerRecentCacheService } from '../../../shared/services/picker-recent-cache.service';
import { EntityDropdownPickerAdapter } from '../../../shared/components/entity-dropdown-picker/entity-dropdown-picker.model';
import { PickerRecentRecord } from '../../../shared/components/entity-dropdown-picker/picker-recent.model';

@Injectable({
  providedIn: 'root'
})
export class SubBrandsService {
  private apiUrl = `${environment.restApiUrl}masterdata/sub-brands`;
  private readonly pickerEntityKey = 'sub-brand';

  constructor(
    private http: HttpClient,
    private recentPickerCache: PickerRecentCacheService
  ) { }

  private handleError(error: HttpErrorResponse) {
    // A client-side or network error occurred.
    if (error.error instanceof ErrorEvent) {
      console.error('An error occurred:', error.error.message);
    } else {
      // The backend returned an unsuccessful response code.
      console.error(
        `Backend returned code ${error.status}, ` +
        `body was: ${JSON.stringify(error.error)}`
      );
    }
    // Return an observable with a user-facing error message.
    return throwError(() => new Error('Something went wrong; please try again later.'));
  }

  getAllSubBrands(params?: any): Observable<Page<SubBrand>> {
    return this.http.get<Page<SubBrand>>(this.apiUrl + '/search', { params: params }).pipe(
      map((page) => this.normalizeSubBrandPage(page)),
      catchError(this.handleError)
    );
  }

  searchSubBrands(query: string, page = 0, size = 25): Observable<Page<SubBrand>> {
    return forkJoin([
      this.searchSubBrandsByField('subBrandName', query, page, size),
      this.searchSubBrandsByField('subBrandCode', query, page, size),
    ]).pipe(
      map(([namePage, codePage]) => this.mergeSubBrandPages(namePage, codePage, page, size)),
      catchError(this.handleError)
    );
  }

  getSubBrandById(id: number): Observable<SubBrand> {
    return this.http.get<SubBrand>(`${this.apiUrl}/${id}`).pipe(
      map((subBrand) => this.normalizeSubBrand(subBrand)),
      catchError(this.handleError)
    );
  }

  loadRecentSubBrands(limit = 10): Observable<SubBrand[]> {
    return from(this.recentPickerCache.loadRecent<SubBrand>(this.pickerEntityKey, limit)).pipe(
      map((items) => items.map((item) => this.normalizeSubBrand(item)))
    );
  }

  getFreshSubBrandById(id: number | string): Observable<SubBrand> {
    return this.getSubBrandById(Number(id));
  }

  rememberSubBrand(subBrand: SubBrand): Observable<void> {
    const normalized = this.normalizeSubBrand(subBrand);
    const record: PickerRecentRecord<SubBrand> = {
      key: `${this.pickerEntityKey}::${this.getSubBrandId(normalized)}`,
      entityKey: this.pickerEntityKey,
      userKey: this.getCurrentUserKey(),
      entityId: this.getSubBrandId(normalized),
      label: `${normalized.subBrandName ?? ''}${normalized.subBrandCode ? ` - ${normalized.subBrandCode}` : ''}`.trim(),
      payload: normalized,
      lastUsedAt: Date.now(),
    };

    return from(this.recentPickerCache.remember(record));
  }

  createSubBrandPickerAdapter(): EntityDropdownPickerAdapter<SubBrand> {
    return {
      entityKey: this.pickerEntityKey,
      loadRecent: (limit?: number) => this.loadRecentSubBrands(limit),
      search: (query: string, page: number, size: number) => this.searchSubBrands(query, page, size),
      getFreshById: (id: number | string) => this.getFreshSubBrandById(id),
      rememberSelection: (subBrand: SubBrand) => this.rememberSubBrand(subBrand),
      getId: (subBrand: SubBrand) => this.getSubBrandId(subBrand),
      getLabel: (subBrand: SubBrand) => `${subBrand.subBrandName ?? ''}${subBrand.subBrandCode ? ` - ${subBrand.subBrandCode}` : ''}`.trim(),
      getSecondaryLabel: (subBrand: SubBrand) => subBrand.brand?.brandName ? `Brand: ${subBrand.brand.brandName}` : '',
      filterBy: 'subBrandName,subBrandCode',
    };
  }

  createSubBrand(brand: Partial<SubBrand>): Observable<SubBrand> {
    return this.http.post<SubBrand>(this.apiUrl, brand).pipe(catchError(this.handleError));
  }

  updateSubBrand(brand: Partial<SubBrand>): Observable<SubBrand> {
    return this.http
      .put<SubBrand>(`${this.apiUrl}/${brand.id}`, brand)
      .pipe(catchError(this.handleError));
  }

  deleteSubBrand(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`).pipe(catchError(this.handleError));
  }

  private getCurrentUserKey(): string {
    try {
      const rawUser = sessionStorage.getItem('appUser');
      if (!rawUser) {
        return 'anonymous';
      }

      const parsed = JSON.parse(rawUser);
      return String(parsed?.id ?? 'anonymous');
    } catch {
      return 'anonymous';
    }
  }

  private searchSubBrandsByField(
    field: 'subBrandName' | 'subBrandCode',
    query: string,
    page: number,
    size: number
  ): Observable<Page<SubBrand>> {
    const params = {
      [field]: query,
      page,
      size,
    };

    return this.http.get<Page<SubBrand>>(`${this.apiUrl}/search`, { params }).pipe(
      catchError(() => of(this.emptySubBrandPage(page, size)))
    );
  }

  private mergeSubBrandPages(
    namePage: Page<SubBrand>,
    codePage: Page<SubBrand>,
    page: number,
    size: number
  ): Page<SubBrand> {
    const merged = new Map<number, SubBrand>();
    [...(namePage.content || []), ...(codePage.content || [])].forEach((subBrand) => {
      const normalized = this.normalizeSubBrand(subBrand);
      const id = this.getSubBrandId(normalized);
      if (id !== null) {
        merged.set(id, normalized);
      }
    });

    const content = Array.from(merged.values()).slice(0, size);
    return {
      ...namePage,
      content,
      number: page,
      size,
      totalElements: merged.size,
      numberOfElements: content.length,
    };
  }

  private emptySubBrandPage(page: number, size: number): Page<SubBrand> {
    return {
      content: [],
      pageable: {
        pageNumber: page,
        pageSize: size,
        sort: { sorted: false, unsorted: true, empty: true },
        offset: page * size,
        paged: true,
        unpaged: false,
      },
      last: true,
      totalPages: 0,
      totalElements: 0,
      size,
      number: page,
      sort: { sorted: false, unsorted: true, empty: true },
      first: page === 0,
      numberOfElements: 0,
      empty: true,
    };
  }

  private normalizeSubBrand(subBrand: SubBrand): SubBrand {
    const id = subBrand?.id ?? subBrand?.subBrandId ?? null;
    return {
      ...subBrand,
      id: id ?? undefined,
      subBrandId: id ?? undefined,
    };
  }

  private normalizeSubBrandPage(page: Page<SubBrand>): Page<SubBrand> {
    return {
      ...page,
      content: (page.content || []).map((subBrand) => this.normalizeSubBrand(subBrand)),
    };
  }

  private getSubBrandId(subBrand: SubBrand): number {
    return Number(subBrand?.id ?? subBrand?.subBrandId);
  }
}
